// Lens domain v2: customer matching, category rules, account-manager seeding,
// billing matching, and order company step rules per Omni review plan section 3.2, 3.3.

import type { Address, CustomerContact, Iso } from "./index.js";
import type { AccountProduct } from "./accounts.js";
import type { Via } from "./via.js";

// ---------- Company Key and Autotask Identifiers ----------

/**
 * Autotask company ID 0 is Phoneware itself and never a customer.
 */
export const AUTOTASK_PHONEWARE_COMPANY_ID = 0;

/**
 * Validates whether an Autotask company ID is an eligible customer ID.
 * Excludes company ID 0 (Phoneware itself).
 */
export function isCustomerCompanyId(id: number): boolean {
 return typeof id === "number" && Number.isInteger(id) && id > 0;
}

/**
 * Exact name matching key per plan section 3.2:
 * Trimmed, case-insensitive, whitespace collapsed.
 */
export function exactName(name: string): string {
 if (!name) return "";
 return name.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Normalizes a company name into a matching key per plan section 3.2:
 * 1. Normalize accents (NFKD), lowercase, and turn '&' and '+' into 'and'.
 * 2. Split on anything that isn't a letter or digit and drop a leading 'the'.
 * 3. While more than one word remains, drop trailing:
 *    inc, incorporated, llc, ltd, limited, corp, corporation, co, company, pc, pllc, plc, lp, llp, pa.
 * 4. Join the words with no spaces.
 */
const TRAILING_COMPANY_SUFFIXES: Record<string, true> = {
 inc: true,
 incorporated: true,
 llc: true,
 ltd: true,
 limited: true,
 corp: true,
 corporation: true,
 co: true,
 company: true,
 pc: true,
 pllc: true,
 plc: true,
 lp: true,
 llp: true,
 pa: true,
};

export function companyKey(name: string): string {
 if (!name) return "";
 const normalized = name
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[&+]/g, " and ");

 const words = normalized
  .replace(/[^a-z0-9]+/g, " ")
  .trim()
  .split(/\s+/)
  .filter((w) => w.length > 0);

 if (words.length > 0 && words[0] === "the") {
  words.shift();
 }

 while (words.length > 1 && TRAILING_COMPANY_SUFFIXES[words[words.length - 1]!]) {
  words.pop();
 }

 return words.join("");
}

// ---------- Category Matching and Nicknames ----------

/**
 * Production category nicknames from plan section 2 item 6.
 * Phoneware and ApexIT have none.
 */
export const CATEGORY_NICKNAMES = [
 "TVC",
 "ALN",
 "ADG",
 "LTG",
 "T4L",
 "SRN",
 "NNI",
 "TWI",
] as const;

export type CategoryNickname = (typeof CATEGORY_NICKNAMES)[number];

/**
 * Nickname table mapping known category names, IDs and aliases to their nicknames.
 */
export const CATEGORY_NICKNAME_TABLE: Readonly<Record<string, string>> = Object.freeze({
 TVC: "TVC",
 "Total Voice Concepts": "TVC",
 ALN: "ALN",
 ADG: "ADG",
 LTG: "LTG",
 "Leverage Technology Group": "LTG",
 T4L: "T4L",
 "Tech 4 Life": "T4L",
 SRN: "SRN",
 NNI: "NNI",
 TWI: "TWI",
 // By category ID
 "100": "TVC",
 "103": "LTG",
 "104": "T4L",
});

export function resolveCategoryNickname(
 category: { id?: number; name: string; nickname?: string | null },
 customTable?: Record<string, string>,
): string | null {
 if (category.nickname && category.nickname.trim().length > 0) {
  return category.nickname.trim();
 }
 const table = customTable ?? CATEGORY_NICKNAME_TABLE;
 if (category.id !== undefined && table[String(category.id)]) {
  return table[String(category.id)]!;
 }
 if (table[category.name]) {
  return table[category.name]!;
 }
 const trimmed = category.name.trim();
 if (table[trimmed]) {
  return table[trimmed]!;
 }
 return null;
}

export type CategoryMatchVia = "exact_name" | "nickname" | "near_exact";

export interface CategoryToDealerCandidate {
 id: string;
 name: string;
 nsReseller?: string | null;
}

export interface CategoryMatchSuggestion {
 dealerId: string;
 dealerName: string;
 reason: "nickname_prefix" | "company_key_contains";
}

export interface CategoryMatchResult {
 matched: {
  dealerId: string;
  via: CategoryMatchVia;
 } | null;
 suggestions: CategoryMatchSuggestion[];
}

/**
 * Evaluates category-to-dealer matching for a single category against dealers.
 * Tiers per section 3.2:
 * 1. Category name equals the dealer's name or NS key.
 * 2. Category nickname equals the dealer's name or NS key.
 * 3. Company keys are equal.
 * Review suggestions:
 * - Dealer name or NS key starts with the nickname (e.g. LTGAZ starts with LTG).
 * - Dealer company key contains the category company key.
 */
export function matchCategoryToDealer(
 category: { id?: number; name: string; nickname?: string | null },
 dealers: CategoryToDealerCandidate[],
 options?: { nicknameTable?: Record<string, string> },
): CategoryMatchResult {
 const normCatName = exactName(category.name);
 const catKey = companyKey(category.name);
 const nickname = resolveCategoryNickname(category, options?.nicknameTable);
 const normNickname = nickname ? exactName(nickname) : null;

 // Tier 1: Category name equals dealer's name or NS key
 const tier1Matches = dealers.filter((d) => {
  return (
   exactName(d.name) === normCatName ||
   (d.nsReseller ? exactName(d.nsReseller) === normCatName : false)
  );
 });
 if (tier1Matches.length === 1) {
  return {
   matched: { dealerId: tier1Matches[0]!.id, via: "exact_name" },
   suggestions: [],
  };
 }
 if (tier1Matches.length > 1) {
  // Ambiguous at tier 1: stop, do not fall through to looser tiers
  return { matched: null, suggestions: [] };
 }

 // Tier 2: Category nickname equals dealer's name or NS key
 if (normNickname) {
  const tier2Matches = dealers.filter((d) => {
   return (
    exactName(d.name) === normNickname ||
    (d.nsReseller ? exactName(d.nsReseller) === normNickname : false)
   );
  });
  if (tier2Matches.length === 1) {
   return {
    matched: { dealerId: tier2Matches[0]!.id, via: "nickname" },
    suggestions: [],
   };
  }
  if (tier2Matches.length > 1) {
   // Ambiguous at tier 2: stop
   return { matched: null, suggestions: [] };
  }
 }

 // Tier 3: Company keys are equal
 if (catKey) {
  const tier3Matches = dealers.filter((d) => {
   return (
    companyKey(d.name) === catKey ||
    (d.nsReseller ? companyKey(d.nsReseller) === catKey : false)
   );
  });
  if (tier3Matches.length === 1) {
   return {
    matched: { dealerId: tier3Matches[0]!.id, via: "near_exact" },
    suggestions: [],
   };
  }
  if (tier3Matches.length > 1) {
   // Ambiguous at tier 3: stop
   return { matched: null, suggestions: [] };
  }
 }

 // Review suggestions when not auto-matched
 const suggestions: CategoryMatchSuggestion[] = [];
 for (const d of dealers) {
  const dExact = exactName(d.name);
  const nsExact = d.nsReseller ? exactName(d.nsReseller) : null;
  const dKey = companyKey(d.name);
  const nsKey = d.nsReseller ? companyKey(d.nsReseller) : null;

  if (normNickname) {
   if (dExact.startsWith(normNickname) || (nsExact && nsExact.startsWith(normNickname))) {
    suggestions.push({
     dealerId: d.id,
     dealerName: d.name,
     reason: "nickname_prefix",
    });
    continue;
   }
  }

  if (catKey && ((dKey && dKey.includes(catKey)) || (nsKey && nsKey.includes(catKey)))) {
   suggestions.push({
    dealerId: d.id,
    dealerName: d.name,
    reason: "company_key_contains",
   });
  }
 }

 return { matched: null, suggestions };
}

/**
 * Batch matching across collections of categories and dealers,
 * enforcing 1:1 uniqueness per tier so ambiguous candidates stop rather than fall through.
 */
export function matchCategoriesToDealers(
 categories: Array<{ id: number; name: string; nickname?: string | null }>,
 dealers: CategoryToDealerCandidate[],
 options?: { nicknameTable?: Record<string, string> },
): Map<number, CategoryMatchResult> {
 const results = new Map<number, CategoryMatchResult>();
 const unlinkedCats = new Set(categories.map((c) => c.id));
 const claimedDealers = new Set<string>();

 // Helper for 1:1 tier check
 const runTier = (
  via: CategoryMatchVia,
  predicate: (
   cat: { id: number; name: string; nickname?: string | null },
   dealer: CategoryToDealerCandidate,
  ) => boolean,
 ) => {
  const pairs: Array<{ catId: number; dealerId: string }> = [];
  const catMatchCounts = new Map<number, number>();
  const dealerMatchCounts = new Map<string, number>();

  for (const cat of categories) {
   if (!unlinkedCats.has(cat.id)) continue;
   for (const dealer of dealers) {
    if (claimedDealers.has(dealer.id)) continue;
    if (predicate(cat, dealer)) {
     pairs.push({ catId: cat.id, dealerId: dealer.id });
     catMatchCounts.set(cat.id, (catMatchCounts.get(cat.id) ?? 0) + 1);
     dealerMatchCounts.set(dealer.id, (dealerMatchCounts.get(dealer.id) ?? 0) + 1);
    }
   }
  }

  // Only link if exactly 1:1 on both sides
  for (const pair of pairs) {
   if (catMatchCounts.get(pair.catId) === 1 && dealerMatchCounts.get(pair.dealerId) === 1) {
    if (unlinkedCats.has(pair.catId) && !claimedDealers.has(pair.dealerId)) {
     results.set(pair.catId, {
      matched: { dealerId: pair.dealerId, via },
      suggestions: [],
     });
     unlinkedCats.delete(pair.catId);
     claimedDealers.add(pair.dealerId);
    }
   }
  }
 };

 // Tier 1: exact_name
 runTier("exact_name", (cat, dealer) => {
  const cNorm = exactName(cat.name);
  return (
   exactName(dealer.name) === cNorm ||
   (dealer.nsReseller ? exactName(dealer.nsReseller) === cNorm : false)
  );
 });

 // Tier 2: nickname
 runTier("nickname", (cat, dealer) => {
  const nick = resolveCategoryNickname(cat, options?.nicknameTable);
  if (!nick) return false;
  const nNorm = exactName(nick);
  return (
   exactName(dealer.name) === nNorm ||
   (dealer.nsReseller ? exactName(dealer.nsReseller) === nNorm : false)
  );
 });

 // Tier 3: near_exact (company key)
 runTier("near_exact", (cat, dealer) => {
  const cKey = companyKey(cat.name);
  if (!cKey) return false;
  return (
   companyKey(dealer.name) === cKey ||
   (dealer.nsReseller ? companyKey(dealer.nsReseller) === cKey : false)
  );
 });

 // For remaining categories, find review suggestions among unclaimed dealers
 const remainingDealers = dealers.filter((d) => !claimedDealers.has(d.id));
 for (const cat of categories) {
  if (unlinkedCats.has(cat.id)) {
   const singleRes = matchCategoryToDealer(cat, remainingDealers, options);
   results.set(cat.id, {
    matched: null,
    suggestions: singleRes.suggestions,
   });
  }
 }

 return results;
}

// ---------- Account-Manager Seeding ----------

export interface ActiveCustomerOwnerInfo {
 ownerResourceId: number | null;
 isActive?: boolean;
}

export interface AccountManagerSeedResult {
 resourceId: number;
 companies: number;
 ofCustomers: number;
}

/**
 * Checks whether an Autotask resource can own a company.
 * Matches adapters/autotask-customers.ts:205-210.
 */
export function canOwnCompany(resource: {
 isActive: boolean;
 licenseType: number;
 userType: number;
}): boolean {
 if (!resource.isActive) return false;
 if (resource.licenseType === 7) return false;
 if (resource.userType === 13 || resource.userType === 24) return false;
 return true;
}

/**
 * Account manager seed rule per plan section 3.2:
 * The most common ownerResourceID among the category's active customers.
 * Seeded only if it's a unique winner, passes eligibility (canOwnCompany),
 * and the dealer has no account manager yet.
 */
export function seedAccountManager(
 customers: ActiveCustomerOwnerInfo[],
 options?: {
  currentDealerOwnerResourceId?: number | null;
  isEligible?: (resourceId: number) => boolean;
 },
): AccountManagerSeedResult | null {
 if (options?.currentDealerOwnerResourceId != null) {
  return null;
 }

 const activeCustomers = customers.filter((c) => c.isActive !== false);
 const totalActive = activeCustomers.length;
 if (totalActive === 0) {
  return null;
 }

 const countsByOwner = new Map<number, number>();
 for (const c of activeCustomers) {
  const rid = c.ownerResourceId;
  if (typeof rid === "number" && rid > 0) {
   countsByOwner.set(rid, (countsByOwner.get(rid) ?? 0) + 1);
  }
 }

 if (countsByOwner.size === 0) {
  return null;
 }

 let topResourceId: number | null = null;
 let topCount = 0;
 let isTie = false;

 for (const [resourceId, count] of countsByOwner.entries()) {
  if (count > topCount) {
   topCount = count;
   topResourceId = resourceId;
   isTie = false;
  } else if (count === topCount) {
   isTie = true;
  }
 }

 if (isTie || topResourceId === null) {
  return null;
 }

 if (options?.isEligible && !options.isEligible(topResourceId)) {
  return null;
 }

 return {
  resourceId: topResourceId,
  companies: topCount,
  ofCustomers: totalActive,
 };
}

// ---------- Billing Account Matching ----------

export type CustomerBillingVia =
 | "billing_map"
 | "name_exact"
 | "name_near_exact"
 | "ops"
 | "order"
 | "account_number";

export interface CustomerBillingCandidate {
 accountNumber: string;
 name: string;
 score: number;
 why: "name" | "phone" | "number" | "parent";
}

export type CustomerBilling =
 | {
  state: "linked";
  accountNumber: string;
  via: CustomerBillingVia;
  at: Iso;
  by: string | null;
 }
 | { state: "none"; via: "created_in_omni" | "ops"; at: Iso; by: string | null }
 | {
  state: "unreviewed";
  candidates: CustomerBillingCandidate[];
  at: Iso;
 };

export function tokenizeCompanyWords(name: string): string[] {
 if (!name) return [];
 const normalized = name
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[&+]/g, " and ")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();
 return normalized.split(/\s+/).filter((w) => w.length > 0);
}

export function tokenJaccard(tokensA: string[], tokensB: string[]): number {
 if (tokensA.length === 0 || tokensB.length === 0) return 0;
 const setA = new Set(tokensA);
 const setB = new Set(tokensB);
 let intersection = 0;
 for (const t of setA) {
  if (setB.has(t)) intersection++;
 }
 const union = new Set([...setA, ...setB]).size;
 return union > 0 ? intersection / union : 0;
}

export function tokenContainment(tokensA: string[], tokensB: string[]): number {
 const setA = new Set(tokensA);
 const setB = new Set(tokensB);
 const shorter = setA.size <= setB.size ? setA : setB;
 const longer = setA.size <= setB.size ? setB : setA;

 // Plan section 3.2: containment needs at least 2 words in the shorter name
 if (shorter.size < 2) return 0;

 let intersection = 0;
 for (const t of shorter) {
  if (longer.has(t)) intersection++;
 }
 return intersection / shorter.size;
}

export function scoreNameSimilarity(nameA: string, nameB: string): number {
 const tokensA = tokenizeCompanyWords(nameA);
 const tokensB = tokenizeCompanyWords(nameB);
 const jaccard = tokenJaccard(tokensA, tokensB);
 const containment = tokenContainment(tokensA, tokensB);
 return Math.max(jaccard, containment);
}

export function normalizePhone10(phone: string | null | undefined): string | null {
 if (!phone) return null;
 const digits = phone.replace(/\D/g, "");
 if (digits.length === 10) return digits;
 if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
 return null;
}

export interface CustomerForBillingMatch {
 autotaskCompanyId: number;
 name: string;
 phone?: string | null;
 billing?: CustomerBilling | null;
 accountNumber?: string | null;
 companyNumber?: string | null;
 parentCompanyId?: number | null;
}

export interface OneBillAccountForBillingMatch {
 accountNumber: string;
 name: string;
 phone?: string | null;
 status?: string;
}

export interface BillingMapLookupEntry {
 autotaskCompanyId: number;
 accountNumber: string;
}

/**
 * Matches customers to OneBill accounts per section 3.2:
 * Tiers:
 * 1. Billing map entry (Autotask id to account number, unique) -> "billing_map".
 * 2. Exact name -> "name_exact".
 * 3. Company key -> "name_near_exact".
 * Certain match must be 1:1, and account not already claimed.
 * Otherwise customer stays unreviewed with up to 3 candidates scoring >= 0.5 on
 * max(tokenJaccard, containment) (containment needs >= 2 words in shorter name) or matching phone.
 * Runs never change existing "linked" or "none" states.
 */
export function matchCustomersToBillingAccounts(
 customers: CustomerForBillingMatch[],
 accounts: OneBillAccountForBillingMatch[],
 billingMapEntries: BillingMapLookupEntry[] = [],
 now: Iso = new Date().toISOString(),
 dismissedAccountNumbers?: Set<string>,
): Map<number, CustomerBilling> {
 const results = new Map<number, CustomerBilling>();
 const unlinkedCustomerIds = new Set<number>();
 const claimedAccounts = new Set<string>();

 // Accounts map by accountNumber
 const accountsByNumber = new Map<string, OneBillAccountForBillingMatch>();
 for (const acc of accounts) {
  accountsByNumber.set(acc.accountNumber, acc);
 }

 // Preserve existing linked or none states
 for (const c of customers) {
  if (c.billing && (c.billing.state === "linked" || c.billing.state === "none")) {
   results.set(c.autotaskCompanyId, c.billing);
   if (c.billing.state === "linked") {
    claimedAccounts.add(c.billing.accountNumber);
   }
  } else {
   unlinkedCustomerIds.add(c.autotaskCompanyId);
  }
 }

 // Index unlinked customers
 const customersById = new Map<number, CustomerForBillingMatch>();
 for (const c of customers) {
  if (unlinkedCustomerIds.has(c.autotaskCompanyId)) {
   customersById.set(c.autotaskCompanyId, c);
  }
 }

 // Tier 0: Account Number tier (runs before billing map tier)
 // Certain when:
 // - Customer's Autotask Number equals a OneBill accountNumber.
 // - Exactly one Autotask company (any type) holds that Number.
 // - Account isn't linked to another customer.
 // - Names score at least 0.5 on max(tokenJaccard, containment).
 // Disagreeing names (< 0.5) become first suggestion with why: "number".
 const companyCountByNumber = new Map<string, number>();
 for (const c of customers) {
  const rawNum = c.accountNumber ?? c.companyNumber;
  if (rawNum) {
   const trimmed = rawNum.trim();
   if (trimmed) {
    companyCountByNumber.set(trimmed, (companyCountByNumber.get(trimmed) ?? 0) + 1);
   }
  }
 }

 const numberSuggestions = new Map<number, CustomerBillingCandidate>();

 for (const cid of Array.from(unlinkedCustomerIds)) {
  const cust = customersById.get(cid)!;
  const rawNum = cust.accountNumber ?? cust.companyNumber;
  const num = rawNum?.trim();
  if (!num) continue;

  const count = companyCountByNumber.get(num) ?? 0;
  const acc = accountsByNumber.get(num);

  if (acc && count === 1 && !claimedAccounts.has(num) && !dismissedAccountNumbers?.has(num)) {
   const nameScore = scoreNameSimilarity(cust.name, acc.name);
   if (nameScore >= 0.5) {
    results.set(cid, {
     state: "linked",
     accountNumber: num,
     via: "account_number",
     at: now,
     by: null,
    });
    unlinkedCustomerIds.delete(cid);
    claimedAccounts.add(num);
   } else {
    numberSuggestions.set(cid, {
     accountNumber: acc.accountNumber,
     name: acc.name,
     score: Number(nameScore.toFixed(3)),
     why: "number",
    });
   }
  }
 }

 // Tier 1: Billing Map entry
 // Map must be 1:1 between Autotask ID and account number
 const bMapByCompanyId = new Map<number, string[]>();
 const bMapByAccountNumber = new Map<string, number[]>();
 for (const entry of billingMapEntries) {
  if (!bMapByCompanyId.has(entry.autotaskCompanyId)) {
   bMapByCompanyId.set(entry.autotaskCompanyId, []);
  }
  bMapByCompanyId.get(entry.autotaskCompanyId)!.push(entry.accountNumber);

  if (!bMapByAccountNumber.has(entry.accountNumber)) {
   bMapByAccountNumber.set(entry.accountNumber, []);
  }
  bMapByAccountNumber.get(entry.accountNumber)!.push(entry.autotaskCompanyId);
 }

 for (const cid of Array.from(unlinkedCustomerIds)) {
  const accNums = bMapByCompanyId.get(cid);
  if (accNums && accNums.length === 1) {
   const accNum = accNums[0]!;
   const companiesForAcc = bMapByAccountNumber.get(accNum);
   if (
    companiesForAcc &&
    companiesForAcc.length === 1 &&
    !claimedAccounts.has(accNum) &&
    accountsByNumber.has(accNum)
   ) {
    results.set(cid, {
     state: "linked",
     accountNumber: accNum,
     via: "billing_map",
     at: now,
     by: null,
    });
    unlinkedCustomerIds.delete(cid);
    claimedAccounts.add(accNum);
   }
  }
 }

 // Tier 2: Exact Name
 // Find 1:1 exact name matches among remaining
 const customersByExactName = new Map<string, number[]>();
 for (const cid of unlinkedCustomerIds) {
  const cust = customersById.get(cid)!;
  const key = exactName(cust.name);
  if (!customersByExactName.has(key)) customersByExactName.set(key, []);
  customersByExactName.get(key)!.push(cid);
 }

 const accountsByExactName = new Map<string, string[]>();
 for (const acc of accounts) {
  if (claimedAccounts.has(acc.accountNumber)) continue;
  const key = exactName(acc.name);
  if (!accountsByExactName.has(key)) accountsByExactName.set(key, []);
  accountsByExactName.get(key)!.push(acc.accountNumber);
 }

 for (const [nameKey, cids] of customersByExactName.entries()) {
  const accNums = accountsByExactName.get(nameKey);
  if (cids.length === 1 && accNums && accNums.length === 1) {
   const cid = cids[0]!;
   const accNum = accNums[0]!;
   if (!claimedAccounts.has(accNum)) {
    results.set(cid, {
     state: "linked",
     accountNumber: accNum,
     via: "name_exact",
     at: now,
     by: null,
    });
    unlinkedCustomerIds.delete(cid);
    claimedAccounts.add(accNum);
   }
  }
 }

 // Tier 3: Company Key (near-exact)
 const customersByCompKey = new Map<string, number[]>();
 for (const cid of unlinkedCustomerIds) {
  const cust = customersById.get(cid)!;
  const key = companyKey(cust.name);
  if (!key) continue;
  if (!customersByCompKey.has(key)) customersByCompKey.set(key, []);
  customersByCompKey.get(key)!.push(cid);
 }

 const accountsByCompKey = new Map<string, string[]>();
 for (const acc of accounts) {
  if (claimedAccounts.has(acc.accountNumber)) continue;
  const key = companyKey(acc.name);
  if (!key) continue;
  if (!accountsByCompKey.has(key)) accountsByCompKey.set(key, []);
  accountsByCompKey.get(key)!.push(acc.accountNumber);
 }

 for (const [key, cids] of customersByCompKey.entries()) {
  const accNums = accountsByCompKey.get(key);
  if (cids.length === 1 && accNums && accNums.length === 1) {
   const cid = cids[0]!;
   const accNum = accNums[0]!;
   if (!claimedAccounts.has(accNum)) {
    results.set(cid, {
     state: "linked",
     accountNumber: accNum,
     via: "name_near_exact",
     at: now,
     by: null,
    });
    unlinkedCustomerIds.delete(cid);
    claimedAccounts.add(accNum);
   }
  }
 }

 // Remaining unlinked customers become "unreviewed" with up to 3 suggestions
 const availableAccounts = accounts.filter((a) => !claimedAccounts.has(a.accountNumber));

 for (const cid of unlinkedCustomerIds) {
  const cust = customersById.get(cid)!;
  const custPhone = normalizePhone10(cust.phone);
  const candidates: CustomerBillingCandidate[] = [];
  const seenAccNumbers = new Set<string>();

  const numSuggestion = numberSuggestions.get(cid);
  if (numSuggestion && !claimedAccounts.has(numSuggestion.accountNumber)) {
   candidates.push(numSuggestion);
   seenAccNumbers.add(numSuggestion.accountNumber);
  }

  if (cust.parentCompanyId) {
   const parentCust = customers.find((c) => c.autotaskCompanyId === cust.parentCompanyId);
   const parentBilling = results.get(cust.parentCompanyId) ?? parentCust?.billing;
   if (parentBilling && parentBilling.state === "linked") {
    const parentAccNum = parentBilling.accountNumber;
    if (!seenAccNumbers.has(parentAccNum)) {
     const parentAcc = accountsByNumber.get(parentAccNum);
     candidates.push({
      accountNumber: parentAccNum,
      name: parentAcc?.name ?? parentCust?.name ?? "",
      score: 1.0,
      why: "parent",
     });
     seenAccNumbers.add(parentAccNum);
    }
   }
  }

  const otherCandidates: CustomerBillingCandidate[] = [];
  for (const acc of availableAccounts) {
   if (seenAccNumbers.has(acc.accountNumber)) continue;
   const accPhone = normalizePhone10(acc.phone);
   if (custPhone && accPhone && custPhone === accPhone) {
    otherCandidates.push({
     accountNumber: acc.accountNumber,
     name: acc.name,
     score: 1.0,
     why: "phone",
    });
    continue;
   }

   const score = scoreNameSimilarity(cust.name, acc.name);
   if (score >= 0.5) {
    otherCandidates.push({
     accountNumber: acc.accountNumber,
     name: acc.name,
     score: Number(score.toFixed(3)),
     why: "name",
    });
   }
  }

  otherCandidates.sort((a, b) => b.score - a.score);
  const remainingSlots = 3 - candidates.length;
  candidates.push(...otherCandidates.slice(0, remainingSlots));

  results.set(cid, {
   state: "unreviewed",
   candidates,
   at: now,
  });
 }

 return results;
}

// ---------- Category Check (Omni Dealer vs Autotask Category) ----------

export interface CategoryCheckInput {
 autotaskCompanyId: number;
 customerName: string;
 customerCategoryId: number | null;
 omniDealerId: string;
 omniDealerName: string;
 omniExpectedCategoryId: number | null;
 categoryException?: { categoryId: number | null; by: string; at: Iso } | null;
 observedCategoryName?: string | null;
 observedAt?: Iso;
}

export interface CategoryReviewItem {
 autotaskCompanyId: number;
 customerName: string;
 omniDealerId: string;
 omniDealerName: string;
 omniExpectedCategoryId: number | null;
 autotaskCategoryId: number | null;
 autotaskCategoryName: string | null;
 observedAt: Iso;
 actions: ["keep_omni", "take_autotask"];
}

export interface CategoryCheckResult {
 matches: boolean;
 isException: boolean;
 reviewItem: CategoryReviewItem | null;
}

/**
 * Checks whether an Autotask company category matches the Omni dealer category.
 * If they differ, generates an ops review item (keep_omni / take_autotask)
 * unless an acknowledged exception is already on record.
 * Never auto-follows.
 */
export function checkCustomerCategory(input: CategoryCheckInput): CategoryCheckResult {
 const matches = input.customerCategoryId === input.omniExpectedCategoryId;
 if (matches) {
  return { matches: true, isException: false, reviewItem: null };
 }

 if (
  input.categoryException &&
  input.categoryException.categoryId === input.customerCategoryId
 ) {
  return { matches: false, isException: true, reviewItem: null };
 }

 const now = input.observedAt ?? new Date().toISOString();
 return {
  matches: false,
  isException: false,
  reviewItem: {
   autotaskCompanyId: input.autotaskCompanyId,
   customerName: input.customerName,
   omniDealerId: input.omniDealerId,
   omniDealerName: input.omniDealerName,
   omniExpectedCategoryId: input.omniExpectedCategoryId,
   autotaskCategoryId: input.customerCategoryId,
   autotaskCategoryName: input.observedCategoryName ?? null,
   observedAt: now,
   actions: ["keep_omni", "take_autotask"],
  },
 };
}

// ---------- Order Company Step Rule ----------

export type CompanyStepAction = "done" | "patch_to_customer" | "hold";

export interface CompanyStepDecision {
 action: CompanyStepAction;
 state: "done" | "pending" | "failed";
 fromType: number | null;
 targetType: 1 | null;
 error: string | null;
}

/**
 * Company step rule for order execution per plan section 2C:
 * - companyType 1 (Customer): done, step complete.
 * - companyType 3 (Prospect): patch to 1 (Customer).
 * - any other companyType: hold the order with error.
 */
export function evaluateCompanyStep(
 companyType: number | null | undefined,
): CompanyStepDecision {
 if (companyType === 1) {
  return {
   action: "done",
   state: "done",
   fromType: 1,
   targetType: 1,
   error: null,
  };
 }
 if (companyType === 3) {
  return {
   action: "patch_to_customer",
   state: "pending",
   fromType: 3,
   targetType: 1,
   error: null,
  };
 }
 const errorMsg =
  companyType == null
   ? "Customer company type is unknown; order held"
   : `Company type ${companyType} cannot be automatically converted to Customer (type 1); order held`;
 return {
  action: "hold",
  state: "failed",
  fromType: companyType ?? null,
  targetType: null,
  error: errorMsg,
 };
}

// ---------- Domain Entity Types (Firestore Shapes per 3.3) ----------

export interface AutotaskCategoryLink {
 id: number;
 name: string;
 nickname: string | null;
 via: CategoryMatchVia | "ops" | "created";
 linkedBy: string | null;
 linkedAt: Iso;
}

export interface AutotaskOwnerSeed {
 resourceId: number;
 companies: number;
 ofCustomers: number;
 at: Iso;
}

export interface AutotaskCategoryClaim {
 categoryId: number;
 dealerId: string;
}

export interface CustomerRecordV2 {
 autotaskCompanyId: number;
 dealerId: string;
 ownerEmail: string | null;
 createdVia?: Via | null;
 createdBy?: string | null;
 createdAt: Iso;
 source: { kind: "import" | "omni" | "ops"; at: Iso; by: string | null };
 summary: {
  name: string;
  phone: string | null;
  address: Address | null;
  companyType: number;
  isActive: boolean;
  categoryId: number | null;
  classificationId: number | null;
  parentCompanyId: number | null;
  ownerResourceId: number | null;
  accountNumber?: string | null;
  refreshedAt: Iso;
  fingerprint: string;
 };
 billing: CustomerBilling;
 categoryException: { categoryId: number | null; by: string; at: Iso } | null;
}

export interface BillingReviewCandidate {
 autotaskCompanyId: number;
 name: string;
 score: number;
 why: "name" | "phone" | "number" | "parent";
}

export interface BillingReviewItem {
 accountNumber: string;
 accountName: string;
 phone: string | null;
 candidates: BillingReviewCandidate[];
 state: "open" | "dismissed";
 observedAt: Iso;
 runId: string;
 decidedBy: string | null;
 decidedAt: Iso | null;
}

export type CustomerStatus =
 | "active"
 | "inactive"
 | "prospect"
 | "dead"
 | "cancelled"
 | "other";

export interface CustomerCategoryCheckStatus {
 matches: boolean;
 expectedCategoryId: number | null;
 autotaskCategoryId: number | null;
 autotaskCategoryName: string | null;
 exception: boolean;
}

export interface CustomerSummary {
 autotaskCompanyId: number;
 dealerId: string;
 name: string;
 phone: string | null;
 address: Address | null;
 companyType: number;
 isActive: boolean;
 status: CustomerStatus;
 platformName: string | null;
 categoryId: number | null;
 classificationId: number | null;
 parentCompanyId: number | null;
 ownerResourceId: number | null;
 ownerEmail: string | null;
 billing: CustomerBilling;
 categoryCheck: CustomerCategoryCheckStatus;
 accountNumber?: string | null;
 createdVia?: Via | null;
 createdBy?: string | null;
 createdAt: Iso;
 updatedAt?: Iso;
 accountProducts: AccountProduct[];
}

export interface CustomerV2 extends CustomerSummary {
 contacts: CustomerContact[];
}

export type CustomerBillingRequest =
 | { action: "link"; accountNumber: string }
 | { action: "confirm_none" }
 | { action: "reset_unreviewed" };

export type CategoryDecisionRequest =
 | { action: "keep"; observedCategoryId: number | null }
 | { action: "move"; observedCategoryId: number | null };

export type BillingReviewActionRequest =
  | { action: "link"; autotaskCompanyId: number }
  | { action: "dismiss" }
  | { action: "reopen" };

export interface AutotaskCategoryStatus {
 id: number;
 name: string;
 nickname: string | null;
 active: boolean;
 isDefault: boolean;
 customerCount: number;
  dealerId: string | null;
  dealerName: string | null;
  ownerPreview?: { resourceId: number; name: string; companies: number; ofCustomers: number } | null;
}

export interface CustomersReview {
 categoryDiffers: Array<{
  autotaskCompanyId: number;
  customerName: string;
  dealerId: string;
  dealerName: string;
  expectedCategoryId: number | null;
  observedCategoryId: number | null;
  observedCategoryName: string | null;
 }>;
 waitingCustomers: Array<{
  autotaskCompanyId: number;
  name: string;
  categoryId: number | null;
  reason: "no_category" | "category_without_dealer" | "inactive_category";
 }>;
 billingReview: BillingReviewItem[];
}
